import React, { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { waLink, waLabel, trackWhatsApp } from "@/lib/wa";

/**
 * Escritorio: burbuja circular 60px abajo a la derecha.
 * Móvil: barra inferior de ancho completo.
 * Aparece tras el 15% de scroll. Pulso cada 5s, máximo 3 veces por sesión.
 */
export default function FloatingCTA() {
  const [visible, setVisible] = useState(false);
  const [pulse, setPulse] = useState(false);
  const pulsos = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const top =
        window.scrollY || doc.scrollTop || document.body.scrollTop || 0;
      const max = Math.max(
        1,
        (doc.scrollHeight || document.body.scrollHeight) -
          (doc.clientHeight || window.innerHeight)
      );
      setVisible(top / max >= 0.15);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const id = setInterval(() => {
      if (pulsos.current >= 3) {
        clearInterval(id);
        return;
      }
      pulsos.current += 1;
      setPulse(true);
      setTimeout(() => setPulse(false), 1500);
    }, 5000);
    return () => clearInterval(id);
  }, [visible]);

  const label = waLabel("flotante");

  return (
    <>
      {/* Móvil: barra inferior */}
      <a
        href={waLink("flotante")}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackWhatsApp("flotante")}
        data-testid="wa-flotante-movil"
        aria-label={`Escribir por WhatsApp: ${label}`}
        className={`md:hidden fixed left-0 right-0 bottom-0 z-50 flex items-center justify-center gap-2 ${
          pulse ? "wa-pulsing" : ""
        }`}
        style={{
          height: 60,
          background: "var(--wa)",
          color: "#fff",
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 700,
          fontSize: 17,
          textDecoration: "none",
          transform: visible ? "translateY(0)" : "translateY(100%)",
          transition: "transform 320ms ease",
          boxShadow: "0 -6px 24px rgba(0,0,0,0.16)",
        }}
      >
        <MessageCircle size={20} strokeWidth={2} aria-hidden="true" />
        {label}
      </a>

      {/* Escritorio: burbuja */}
      <a
        href={waLink("flotante")}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackWhatsApp("flotante")}
        data-testid="wa-flotante-desktop"
        aria-label={`Escribir por WhatsApp: ${label}`}
        className={`hidden md:flex fixed items-center justify-center rounded-full z-50 ${
          pulse ? "wa-pulsing" : ""
        }`}
        style={{
          right: 24,
          bottom: 24,
          width: 60,
          height: 60,
          background: "var(--wa)",
          color: "#fff",
          boxShadow: "0 10px 28px rgba(37,211,102,0.4)",
          opacity: visible ? 1 : 0,
          pointerEvents: visible ? "auto" : "none",
          transform: visible ? "scale(1)" : "scale(0.7)",
          transition: "opacity 280ms ease, transform 280ms ease",
        }}
        title={label}
      >
        <MessageCircle size={28} strokeWidth={2} aria-hidden="true" />
      </a>
    </>
  );
}
